import _ from 'lodash';

import { PortalPublication } from '../PortalPublication';
import { LanguageType } from '@lifeserver/shared';

import { getTranslatableFields } from '../translationUtils';

const TRANSLATION_TABLE_META_KEYS = new Set(['$type', '$key']);

type LocalizationMapping = {
    path: string;
    portalLocalizationId: string;
};

function getLocalizationMappings(
    publication: PortalPublication,
): Map<string, string> {
    return new Map(
        publication.post.postLocalizations
            ?.filter(
                (loc): loc is LocalizationMapping =>
                    typeof loc.path === 'string' &&
                    typeof loc.portalLocalizationId === 'string',
            )
            .map((loc) => [loc.path, loc.portalLocalizationId]) ?? [],
    );
}

function getTableText(
    tableValue: unknown,
    language: string,
): string | undefined {
    if (tableValue && typeof tableValue === 'object') {
        const text = (tableValue as Record<string, unknown>)[language];
        return typeof text === 'string' ? text : undefined;
    }
    return undefined;
}

function hasAnyTableText(tableValue: unknown): boolean {
    if (!tableValue || typeof tableValue !== 'object') {
        return false;
    }

    return Object.entries(tableValue).some(([key, value]) => {
        return (
            !TRANSLATION_TABLE_META_KEYS.has(key) &&
            typeof value === 'string' &&
            value.trim().length > 0
        );
    });
}

function resolveText(
    localization: Record<string, string> | undefined,
    tableValue: unknown,
    language: string,
): string {
    return localization?.[language] ?? getTableText(tableValue, language) ?? '';
}

export type PublicationTranslationIssue = {
    unmappedPaths: string[];
    missingSelectedLanguagePaths: string[];
    missingFallbackPaths: string[];
};

export function getPublicationTranslationIssue(
    publication: PortalPublication,
    selectedLanguage: LanguageType,
    localizationsById: Record<string, Record<string, string>>,
): PublicationTranslationIssue | null {
    const payload = publication.post.payload as object;
    const translatablePaths = getTranslatableFields(payload).map((path) =>
        path.join('.'),
    );
    if (translatablePaths.length === 0) {
        return null;
    }

    const pathToLocalizationId = getLocalizationMappings(publication);

    const hasMappedLocalizations = pathToLocalizationId.size > 0;
    const unmappedPaths: string[] = [];
    const missingSelectedLanguagePaths: string[] = [];
    const missingFallbackPaths: string[] = [];

    for (const path of translatablePaths) {
        const mappedLocalizationId = pathToLocalizationId.get(path);
        const tableValue = _.get(payload, path) as
            | Record<string, unknown>
            | undefined;

        if (!mappedLocalizationId && hasMappedLocalizations) {
            unmappedPaths.push(path);
        }

        const localization = mappedLocalizationId
            ? localizationsById[mappedLocalizationId]
            : undefined;

        const selectedLanguageText = resolveText(
            localization,
            tableValue,
            selectedLanguage,
        );
        const fallbackText = resolveText(localization, tableValue, 'en');

        if (selectedLanguageText.trim().length === 0) {
            missingSelectedLanguagePaths.push(path);
        }

        if (
            selectedLanguageText.trim().length === 0 &&
            fallbackText.trim().length === 0
        ) {
            if (!hasAnyTableText(tableValue) && !localization) {
                missingFallbackPaths.push(path);
            }
        }
    }

    if (
        unmappedPaths.length === 0 &&
        missingSelectedLanguagePaths.length === 0 &&
        missingFallbackPaths.length === 0
    ) {
        return null;
    }

    return {
        unmappedPaths,
        missingSelectedLanguagePaths,
        missingFallbackPaths,
    };
}

export function getPublicationTranslationIssueTitle(
    issue: PublicationTranslationIssue,
): string {
    const fragments: string[] = [];

    if (issue.unmappedPaths.length > 0) {
        fragments.push(
            `Unmapped fields: ${issue.unmappedPaths.slice(0, 2).join(', ')}`,
        );
    }
    if (issue.missingSelectedLanguagePaths.length > 0) {
        fragments.push(
            `Missing selected language: ${issue.missingSelectedLanguagePaths
                .slice(0, 2)
                .join(', ')}`,
        );
    }
    if (issue.missingFallbackPaths.length > 0) {
        fragments.push(
            `No selected-language/EN fallback: ${issue.missingFallbackPaths
                .slice(0, 2)
                .join(', ')}`,
        );
    }

    return fragments.join(' | ');
}
